import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, ExternalLink, ArrowRight, ChevronRight, Calendar, Sparkles, Zap, Star } from 'lucide-react';
import { LOGO_URL, SOCIAL_LINKS, WHATSAPP_URL, BOOKING_URL, EMS_SITE_URL } from '../data/constants';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

export const Footer = () => {
  const { t, language } = useLanguage();
  
  return (
    <footer className="relative bg-black overflow-hidden" data-testid="footer" role="contentinfo">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-pink-500/15 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary/5 via-transparent to-transparent rounded-full" />
      </div>

      {/* Decorative Top Border */}
      <div className="relative h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Brand Section - Full Width on Mobile */}
          <div className="lg:col-span-5 space-y-8">
            {/* Logo with Glow Effect */}
            <div className="group inline-block">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full" />
                <img 
                  src={LOGO_URL} 
                  alt="Body-Up Personal Training Nyon" 
                  className="relative h-14 w-auto transition-transform duration-300 group-hover:scale-105" 
                  width="160" 
                  height="56"
                  loading="lazy"
                />
              </div>
            </div>
            
            <p className="text-gray-400 text-lg leading-relaxed max-w-md">
              {t.footer.description}
            </p>
            
            {/* Rating Badge - Premium Style */}
            <div className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-white/10 to-white/5 border border-white/20 rounded-2xl backdrop-blur-sm">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-lg">4.9</span>
                <span className="text-gray-400 text-sm">{t.footer.onGoogle}</span>
              </div>
            </div>
            
            {/* Social Media - Premium Animated Icons */}
            <div className="flex items-center gap-4">
              <span className="text-gray-500 text-sm mr-2">{t.footer.followUs || "Suivez-nous"}</span>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="group/social relative w-12 h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center overflow-hidden transition-all duration-500 hover:scale-110 hover:border-pink-500/50"
                aria-label="Instagram"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 opacity-0 group-hover/social:opacity-100 transition-opacity duration-500" />
                <Instagram className="w-5 h-5 text-white relative z-10" />
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="group/social relative w-12 h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center overflow-hidden transition-all duration-500 hover:scale-110 hover:border-blue-500/50"
                aria-label="Facebook"
              >
                <div className="absolute inset-0 bg-blue-600 opacity-0 group-hover/social:opacity-100 transition-opacity duration-500" />
                <Facebook className="w-5 h-5 text-white relative z-10" />
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="group/social relative w-12 h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center overflow-hidden transition-all duration-500 hover:scale-110 hover:border-blue-600/50"
                aria-label="LinkedIn"
              >
                <div className="absolute inset-0 bg-blue-700 opacity-0 group-hover/social:opacity-100 transition-opacity duration-500" />
                <Linkedin className="w-5 h-5 text-white relative z-10" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group/social relative w-12 h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center overflow-hidden transition-all duration-500 hover:scale-110 hover:border-green-500/50"
                aria-label="WhatsApp"
              >
                <div className="absolute inset-0 bg-green-500 opacity-0 group-hover/social:opacity-100 transition-opacity duration-500" />
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white relative z-10">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                <ChevronRight className="w-4 h-4 text-white" />
              </div>
              {t.footer.navigation}
            </h4>
            <ul className="space-y-4">
              {[
                { to: "/", label: t.nav.home },
                { to: "/about", label: t.nav.about },
                { to: "/services", label: t.nav.services },
                { to: "/tarifs", label: language === 'fr' ? "Tarifs" : "Pricing", highlight: true },
                { to: "/offres", label: language === 'fr' ? "Offres en ligne & App" : "Online plans & App" },
                { to: "/blog", label: "Blog" },
                { to: "/contact", label: t.nav.contact },
                { to: "/faq", label: "FAQ", badge: true }
              ].map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.to} 
                    data-testid={`footer-link-${link.to.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    className={`group flex items-center gap-2 transition-all duration-300 ${link.highlight ? 'text-white font-semibold hover:text-primary' : 'text-gray-400 hover:text-white'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${link.highlight ? 'bg-primary group-hover:scale-150' : 'bg-primary/50 group-hover:bg-primary group-hover:scale-150'}`} />
                    <span>{link.label}</span>
                    {link.badge && (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-primary to-pink-500 text-white rounded-full">NEW</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              {t.footer.contact}
            </h4>
            <div className="space-y-4">
              {/* Phone */}
              <a 
                href="tel:+41786044423" 
                className="group flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-primary/50 hover:from-primary/10 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-pink-500/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-pink-500/30 transition-all">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t.footer.phone || "Téléphone"}</div>
                  <div className="text-white font-medium">+41 78 604 44 23</div>
                </div>
              </a>
              
              {/* Email */}
              <a 
                href="mailto:info@body-up.ch" 
                className="group flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-primary/50 hover:from-primary/10 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-pink-500/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-pink-500/30 transition-all">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t.footer.email || "Email"}</div>
                  <div className="text-white font-medium">info@body-up.ch</div>
                </div>
              </a>
              
              {/* Address */}
              <a 
                href="https://maps.google.com/?q=Route+des+Tattes+d'Oie+95,+1260+Nyon" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-primary/50 hover:from-primary/10 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-pink-500/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-pink-500/30 transition-all">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t.footer.address || "Adresse"}</div>
                  <div className="text-white font-medium">Route des Tattes d'Oie 95</div>
                  <div className="text-gray-400 text-sm">1260 Nyon, {language === 'fr' ? 'Suisse' : 'Switzerland'}</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="relative py-8 px-8 mb-8 rounded-2xl bg-gradient-to-r from-primary/20 via-pink-500/10 to-primary/20 border border-primary/30 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-pink-500/10 animate-pulse-slow" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shadow-lg shadow-primary/30">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h5 className="text-white font-bold text-lg">{t.footer.readyTransform || "Prêt à transformer votre corps ?"}</h5>
                <p className="text-gray-400 text-sm">{t.footer.bookNow || "Réservez votre consultation gratuite maintenant"}</p>
              </div>
            </div>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white px-8 py-6 text-base font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:scale-105 transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                {t.footer.bookButton || "Réserver"}
              </Button>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-white/10">
          {/* SEO Pages Links - Improved Contrast for Accessibility */}
          <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4 mb-4">
            <span className="text-gray-400 text-xs">{t.footer.seoLinks?.title || "Nos services à Nyon"} :</span>
            <Link 
              to="/service/personal-training" 
              className="text-gray-400 hover:text-primary text-xs transition-colors underline-offset-2 hover:underline"
            >
              {t.footer.seoLinks?.coachSportif || "Coach Sportif Nyon"}
            </Link>
            <span className="text-gray-500 text-xs">•</span>
            <Link 
              to="/blog/perte-poids-nyon-coaching" 
              className="text-gray-400 hover:text-primary text-xs transition-colors underline-offset-2 hover:underline"
            >
              {t.footer.seoLinks?.pertePoids || "Perte de Poids Nyon"}
            </Link>
            <span className="text-gray-500 text-xs">•</span>
            <Link 
              to="/service/ems-training" 
              className="text-gray-400 hover:text-primary text-xs transition-colors underline-offset-2 hover:underline"
            >
              {t.footer.seoLinks?.emsNyon || "EMS Training Nyon"}
            </Link>
          </div>
          {/* SEO Local Text - Improved Contrast */}
          <p className="text-center text-gray-400 text-xs mb-4">
            {t.seo?.footerLocal || "Coach sportif Nyon | Personal Trainer Vaud | Fitness privé La Côte"}
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm">
              <p className="text-gray-400">
                © {new Date().getFullYear()} {t.footer.copyright}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="hidden md:inline text-gray-500">•</span>
                <Link to={language === 'en' ? '/privacy-policy' : '/politique-confidentialite'} className="text-gray-400 hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-privacy-link">
                  {language === 'en' ? 'Privacy Policy' : 'Politique de confidentialité'}
                </Link>
                <span className="text-gray-500">•</span>
                <Link to={language === 'en' ? '/cookie-policy' : '/politique-cookies'} className="text-gray-400 hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-cookie-policy-link">
                  {language === 'en' ? 'Cookie Policy' : 'Politique cookies'}
                </Link>
                <span className="text-gray-500">•</span>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('bodyup:open-consent'))}
                  className="text-gray-400 hover:text-primary transition-colors underline-offset-2 hover:underline"
                  data-testid="footer-manage-cookies-btn"
                >
                  {language === 'en' ? 'Manage cookies' : 'Gérer mes cookies'}
                </button>
                <span className="text-gray-500">•</span>
                <Link to="/terms" className="text-gray-400 hover:text-primary transition-colors underline-offset-2 hover:underline">
                  {t.footer.terms || "CGV"}
                </Link>
              </div>
            </div>
            <a
              href={EMS_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-primary hover:text-white transition-all duration-300 text-sm font-medium"
            >
              <Zap className="w-4 h-4 group-hover:animate-pulse" />
              <span>{t.emsBadge.discover}</span>
              <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to top on route change - Enhanced version
